// Hook

import { useState } from "react";

// credit https://usehooks.com/useLocalStorage/
const useLocalStorage = <T>(key: string, initialValue: T): [T, (val: T | ((val: T) => T)) => void] => {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === "undefined") {
      return initialValue;
    }
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      if (!item) return initialValue;

      // Parse stored json or if none return initialValue
      const parsed = JSON.parse(item) as T;
      return parsed;
    } catch (error) {
      // If error, log it, clear corrupted data, and return initialValue
      console.warn(`Failed to parse localStorage key "${key}":`, error);
      try {
        window.localStorage.removeItem(key);
      } catch {
        // Ignore errors during cleanup
      }
      return initialValue;
    }
  });
  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      if (typeof window !== "undefined") {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      // Log the error but don't crash - localStorage might be full or unavailable
      console.warn(`Failed to save to localStorage key "${key}":`, error);
    }
  };
  return [storedValue, setValue];
};

export default useLocalStorage;
